/**
 * Hook V2 per duplicare una stratigrafia.
 *
 * Crea una nuova row in `stratigraphies` clonando tutti i campi tranne:
 * - `id` / `created_at` / `updated_at` (auto)
 * - `is_certified` → false (la copia eredita la composizione ma NON la
 *   certificazione: l'utente parte da custom)
 * - `certification_*` → svuotati per coerenza con `is_certified=false`
 * - `name` → suffisso " (copia)" per distinguere
 * - `user_id` / `organization_id` → l'utente corrente (la copia e' sua,
 *   non del proprietario originale del catalogo)
 *
 * Poi clona tutti i `layers` dell'originale puntando alla nuova
 * stratigraphy_id, preservando position/thickness/material/viti/cost.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';

const STRATIGRAPHY_FIELDS_TO_COPY = [
  'type',
  'description',
  'total_thickness',
  'weight_per_sqm',
  'thermal_performance',
  'acoustic_performance',
  'fire_resistance_class',
  'cost_per_sqm',
  'installation_time_per_sqm',
  'material_cost_per_sqm',
  'screw_cost_per_sqm',
  'labor_cost_per_sqm',
  'comprehensive_cost_per_sqm',
  'stud_spacing_mm',
  'frame_coupling',
] as const;

const LAYER_FIELDS_TO_COPY = [
  'material_id',
  'position',
  'thickness',
  'inter_axis',
  'screw_material_id',
  'screw_quantity',
  'screw_cost_per_sqm',
  'anchor_material_id',
  'material_cost_per_sqm',
  'installation_time_minutes',
  'labor_cost_per_hour',
  'labor_cost_per_sqm',
  // Il consumo dichiarato è parte della parete: senza copiarlo, la copia di un
  // placcaggio tornerebbe al consumo tipico del materiale e costerebbe diverso
  // dall'originale.
  'material_quantity_per_sqm',
] as const;

/**
 * Input della duplicazione.
 *
 * La forma a stringa resta valida per i chiamanti storici (card catalogo,
 * configuratore parete e controsoffitto). La forma a oggetto serve alla
 * modifica di una parete DA UN PREVENTIVO: lì la copia deve portare il numero
 * di offerta nel nome, e il toast lo dà il chiamante perché fa parte di una
 * sequenza più lunga (duplica → riaggancia la riga → apri il configuratore).
 */
export type DuplicateInput = string | { sourceId: string; name?: string; silent?: boolean };

export function useDuplicateStratigraphy() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { currentOrganizationId } = useCurrentOrganization();

  const mutation = useMutation({
    mutationFn: async (input: DuplicateInput): Promise<{ id: string }> => {
      const { sourceId, name: customName, silent } = typeof input === 'string'
        ? { sourceId: input, name: undefined, silent: false }
        : input;
      if (!user) throw new Error('Utente non autenticato');
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione attiva');

      // 1. Carica la stratigrafia originale (full row)
      const { data: source, error: srcErr } = await supabase
        .from('stratigraphies')
        .select('*')
        .eq('id', sourceId)
        .maybeSingle();
      if (srcErr) throw srcErr;
      if (!source) throw new Error('Stratigrafia non trovata');

      // 2. Costruisce insert payload — solo campi clonabili + override
      const insertPayload: Record<string, unknown> = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...STRATIGRAPHY_FIELDS_TO_COPY.reduce((acc, f) => ({ ...acc, [f]: (source as any)[f] }), {}),
        name: customName ?? `${(source as { name?: string }).name ?? 'Stratigrafia'} (copia)`,
        is_certified: false,
        certification_id: null,
        user_id: user.id,
        organization_id: currentOrganizationId,
      };

      const { data: created, error: insErr } = await supabase
        .from('stratigraphies')
        .insert(insertPayload)
        .select('id')
        .single();
      if (insErr) throw insErr;
      if (!created?.id) throw new Error('Inserimento copia fallito');

      // 3. Carica i layers dell'originale
      const { data: layers, error: layErr } = await supabase
        .from('layers')
        .select('*')
        .eq('stratigraphy_id', sourceId);
      if (layErr) throw layErr;

      // 4. Clona i layers verso il nuovo stratigraphy_id
      if (layers && layers.length > 0) {
        const layerPayload = layers.map((l) => ({
          stratigraphy_id: created.id,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...LAYER_FIELDS_TO_COPY.reduce((acc, f) => ({ ...acc, [f]: (l as any)[f] }), {}),
        }));
        const { error: layInsErr } = await supabase.from('layers').insert(layerPayload);
        if (layInsErr) throw layInsErr;
      }

      return { id: created.id };
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: ['stratigraphies'] });
      qc.invalidateQueries({ queryKey: ['unified-stratigraphies'] });
      qc.invalidateQueries({ queryKey: ['certified-stratigraphies'] });
      const silent = typeof input === 'object' && input.silent;
      if (!silent) toast.success('Parete duplicata. Apri per modificarla.');
    },
    onError: (err: Error) => {
      console.error('[useDuplicateStratigraphy] error:', err);
      toast.error(err.message || 'Errore durante la duplicazione');
    },
  });

  return {
    duplicateStratigraphy: mutation.mutateAsync,
    isDuplicating: mutation.isPending,
  };
}
